import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, UncontrolledTooltip } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import FormField from '../../../models/FormField';
import OrderCheckoutProduct from '../../../models/order/checkout/OrderCheckoutProduct';
import Product from '../../../models/Product';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import Icon from '../../common/Icon';
import QuantityFieldGroup from '../../common/QuantityFieldGroup';
import FieldError from '../../form/FieldError';

/**
 * Can be removed after we have refactored
 * additional services business logic from AddToCart component
 * into it's own component that wraps AddToCart component with
 * it's own business logic.
 */
const AddToCartBasic = ({
  configStore,
  field,
  onAddToCartClick,
  onUpdateProductQuantity,
  loading,
  maxQuantity,
  disabled,
  product,
  quantityStep,
  tooltipMessage,
  unit,
  customError,
  onBlur,
  onFocus,
}) => {
  const buttonElement = useRef(null);

  const handleAddToCartClick = (_) => {
    const quantity = field.value / quantityStep;
    onAddToCartClick(quantity);
  };

  const renderButton = () => {
    return (
      <Button
        color="primary"
        className="AddToCart__button"
        disabled={ifCartButtonDisabled()}
        onClick={handleAddToCartClick}
        innerRef={buttonElement}
      >
        <Icon
          name={loading ? 'circle-o-notch' : 'shopping-cart'}
          spin={loading}
        />
        <FormattedMessage {...globalTranslations.addToCartButton} />
      </Button>
    );
  };

  const ifCartButtonDisabled = () => {
    return loading || disabled;
  };

  const handleUpdateProductQuantity = (newValue) => {
    onUpdateProductQuantity && onUpdateProductQuantity(newValue);
  };

  return (
    <div className="AddToCart">
      <div className="form-inline">
        <QuantityFieldGroup
          key={product.id}
          className="AddToCart__quantity-group"
          disabled={disabled}
          field={field}
          defaultValue={Number(field.value)}
          maxQuantity={maxQuantity}
          quantityStep={quantityStep}
          unit={unit}
          onUpdateProductQuantity={handleUpdateProductQuantity}
          onBlur={onBlur && onBlur}
          onFocus={onFocus && onFocus}
        />
        {configStore.siteConfig.isWebStore &&
          onAddToCartClick &&
          renderButton()}
        <FieldError field={field} customError={customError} />
      </div>
      {tooltipMessage && (
        <UncontrolledTooltip placement="top" target={buttonElement?.current}>
          {tooltipMessage}
        </UncontrolledTooltip>
      )}
    </div>
  );
};

AddToCartBasic.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  // object means multi subproduct since it's not actual MST-model
  product: PropTypes.oneOfType([
    modelOf(Product),
    modelOf(OrderCheckoutProduct),
    PropTypes.object,
  ]).isRequired,
  field: modelOf(FormField).isRequired,
  maxQuantity: PropTypes.number.isRequired,
  quantityStep: PropTypes.number.isRequired,
  onUpdateProductQuantity: PropTypes.func,
  customError: PropTypes.string,
  unit: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onAddToCartClick: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  tooltipMessage: PropTypes.node,
};

export default inject('configStore')(observer(AddToCartBasic));
